import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [captured, setCaptured] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (open && !sessionId) {
      api
        .post("/chat/session", { referrer: window.location.href })
        .then((r) => {
          setSessionId(r.data.session_id);
          setMessages([{ role: "assistant", content: r.data.greeting }]);
        })
        .catch(() => toast.error("Couldn't start chat"));
    }
  }, [open, sessionId]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const send = async () => {
    if (!input.trim() || sending || !sessionId) return;
    const text = input.trim();
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    setSending(true);
    try {
      const r = await api.post("/chat/message", { session_id: sessionId, message: text });
      setMessages((m) => [...m, { role: "assistant", content: r.data.reply }]);
      if (r.data.lead_captured && !captured) {
        setCaptured(true);
        toast.success("You're in — a coach will follow up shortly.");
      }
    } catch (e) {
      toast.error("Message failed. Try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="chat-widget-open-btn"
          className="fixed bottom-6 right-6 z-50 rounded-full flex items-center gap-2 pl-4 pr-5 py-3 shadow-[0_10px_30px_rgba(22,51,37,0.28)] text-[#f9f8f6]"
          style={{ background: "#163325", transition: "transform 180ms ease, box-shadow 180ms ease" }}
          onMouseEnter={(e) => (e.currentTarget.style.transform = "translateY(-2px)")}
          onMouseLeave={(e) => (e.currentTarget.style.transform = "translateY(0)")}
        >
          <MessageCircle className="w-5 h-5" strokeWidth={1.75} />
          <span className="text-sm">Chat with Coachly</span>
        </button>
      )}
      {open && (
        <div
          data-testid="chat-widget-panel"
          className="fixed bottom-6 right-6 z-50 w-[92vw] sm:w-[380px] h-[560px] rounded-2xl border border-[#e5e2da] bg-white flex flex-col overflow-hidden"
          style={{ boxShadow: "0 20px 60px rgba(22,51,37,0.18)" }}
        >
          <div
            className="px-4 py-3 flex items-center justify-between border-b border-[#e5e2da]"
            style={{ background: "#163325" }}
          >
            <div className="flex items-center gap-2 text-[#f9f8f6]">
              <div className="w-8 h-8 rounded-full flex items-center justify-center font-serif-display text-lg" style={{ background: "#d4a373", color: "#163325" }}>C</div>
              <div>
                <div className="font-medium text-sm">Coachly</div>
                <div className="text-[10px] tracking-wider uppercase text-[#d4a373]">Online · replies instantly</div>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              data-testid="chat-widget-close-btn"
              className="text-[#f9f8f6]/80 hover:text-white p-1 rounded"
              aria-label="Close chat"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 dash-scroll" data-testid="chat-messages">
            {messages.map((m, i) => (
              <div
                key={i}
                className={`max-w-[85%] text-sm leading-relaxed ${
                  m.role === "user" ? "ml-auto" : ""
                }`}
              >
                <div
                  className="px-3.5 py-2.5 rounded-2xl"
                  style={
                    m.role === "user"
                      ? { background: "#163325", color: "#f9f8f6", borderBottomRightRadius: 6 }
                      : { background: "#ebe8e0", color: "#1c1c1c", borderBottomLeftRadius: 6 }
                  }
                >
                  {m.content}
                </div>
              </div>
            ))}
            {sending && (
              <div className="text-sm text-[#5c5c5c] flex gap-1 items-center">
                <span className="w-1.5 h-1.5 rounded-full bg-[#163325] animate-pulse" />
                <span className="w-1.5 h-1.5 rounded-full bg-[#163325] animate-pulse" style={{ animationDelay: "150ms" }} />
                <span className="w-1.5 h-1.5 rounded-full bg-[#163325] animate-pulse" style={{ animationDelay: "300ms" }} />
              </div>
            )}
          </div>
          <div className="border-t border-[#e5e2da] p-3 flex gap-2">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Type a message…"
              data-testid="chat-widget-input"
              className="flex-1 rounded-full border border-[#e5e2da] bg-[#f9f8f6] px-4 py-2 text-sm outline-none focus:border-[#163325]"
              style={{ transition: "border-color 160ms" }}
            />
            <button
              onClick={send}
              disabled={sending || !input.trim()}
              data-testid="chat-widget-send-btn"
              className="w-10 h-10 rounded-full flex items-center justify-center text-[#f9f8f6] disabled:opacity-50"
              style={{ background: "#163325" }}
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
